import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, SendDiagonal } from 'iconoir-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import { criarReserva, getLojaInfo } from '../services/api';
import Lottie from 'lottie-react';
import fazerReservaAnimation from '../assets/fazer-reserva.json';

const RECAPTCHA_SITE_KEY = '6LfAttwsAAAAADUPf4ZvT-5PSpMix2QRJJSTETRh';

// Bitmask dos dias da semana
const DIAS_BITMASK = [1, 2, 4, 8, 16, 32, 64]; // DOM, SEG, TER, QUA, QUI, SEX, SAB
const NOMES_DIAS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];

// Verifica se um dia da semana é permitido para reserva
const isDiaPermitido = (dataStr, diasPermitidos) => {
  if (!dataStr) return true;
  const date = new Date(dataStr + 'T12:00:00');
  const diaSemana = date.getDay(); // 0=DOM, 1=SEG, ..., 6=SAB
  const bitmask = DIAS_BITMASK[diaSemana];
  return (diasPermitidos & bitmask) !== 0;
};

// Gera slots entre horário inicial e final com intervalo configurável
// Suporta horários que cruzam meia-noite (ex: 17:00 - 02:00)
const gerarSlots = (hi, hf, intervalo = 30) => {
  const slots = [];
  if (!hi || !hf) return slots;
  const [hInicio, mInicio] = hi.split(':').map(Number);
  const [hFim, mFim] = hf.split(':').map(Number);
  let minAtual = hInicio * 60 + mInicio;
  let minFim = hFim * 60 + mFim;

  // Se horário final é menor que inicial, cruza meia-noite
  // Adiciona 24h (1440 min) ao horário final
  if (minFim <= minAtual) {
    minFim += 1440;
  }

  while (minAtual < minFim) {
    // Normaliza para 24h (caso passe de meia-noite)
    const minNormalizado = minAtual % 1440;
    const h = Math.floor(minNormalizado / 60);
    const m = minNormalizado % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    minAtual += intervalo;
  }
  return slots;
};

const Reserva = ({ brand }) => {
  const navigate = useNavigate();

  // Carregar reCAPTCHA v3 apenas nesta página
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Remover script e badge ao sair da página
      document.head.removeChild(script);
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.remove();
    };
  }, []);

  const [horariosLoja, setHorariosLoja] = useState([]);
  const [intervaloMinutos, setIntervaloMinutos] = useState(30);
  const [brandLogoUrl, setBrandLogoUrl] = useState(null);
  const [nomeLoja, setNomeLoja] = useState('');
  const [diasPermitidosReserva, setDiasPermitidosReserva] = useState(127); // 127 = todos os dias

  // Carrega horários de funcionamento da loja
  useEffect(() => {
    const fetchHorarios = async () => {
      try {
        const res = await getLojaInfo(brand);
        // Verificar fechamento temporário
        if (res?.loja?.fechamento_temporario?.ativo) {
          navigate('/', { replace: true });
          return;
        }
        if (res?.loja?.horarios) {
          setHorariosLoja(res.loja.horarios);
        }
        if (res?.loja?.logo_url) {
          setBrandLogoUrl(res.loja.logo_url);
        }
        if (res?.loja?.nome) {
          setNomeLoja(res.loja.nome);
        }
        if (res?.loja?.configuracoes?.reserva_intervalo_minutos) {
          setIntervaloMinutos(res.loja.configuracoes.reserva_intervalo_minutos);
        } else if (res?.loja?.reserva_intervalo_minutos) {
          setIntervaloMinutos(res.loja.reserva_intervalo_minutos);
        }
        // Carregar dias permitidos para reserva
        if (res?.loja?.configuracoes?.reserva_dias_permitidos !== undefined) {
          setDiasPermitidosReserva(res.loja.configuracoes.reserva_dias_permitidos);
        }
      } catch (e) {}
    };
    fetchHorarios();
  }, [brand]);

  const [formData, setFormData] = useState({
    nome_cliente: '',
    celular: '',
    data_reserva: '',
    horario: '',
    quantidade_pessoas: '',
    observacoes: ''
  });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErro('');
  };

  const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const validar = () => {
    if (!formData.nome_cliente.trim() || formData.nome_cliente.trim().length < 2) return 'Informe seu nome (mínimo 2 caracteres)';
    if (!formData.celular.trim()) return 'Informe seu celular';
    if (!formData.data_reserva) return 'Selecione a data da reserva';
    if (formData.data_reserva < getTodayString()) return 'A data deve ser hoje ou futura';
    // Validar se o dia da semana é permitido para reservas
    if (!isDiaPermitido(formData.data_reserva, diasPermitidosReserva)) {
      const date = new Date(formData.data_reserva + 'T12:00:00');
      const nomeDia = NOMES_DIAS[date.getDay()];
      return `Reservas não são aceitas para ${nomeDia}. Por favor, escolha outro dia.`;
    }
    if (!formData.horario) return 'Selecione o horário';
    if (!formData.quantidade_pessoas || parseInt(formData.quantidade_pessoas) < 1) return 'Informe a quantidade de pessoas';
    if (formData.observacoes && formData.observacoes.length > 500) return 'Observações devem ter no máximo 500 caracteres';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const erroValidacao = validar();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    setEnviando(true);
    setErro('');

    try {
      // Executar reCAPTCHA v3
      const captchaToken = await new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
          reject(new Error('reCAPTCHA não carregado'));
          return;
        }
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'reserva' })
            .then(resolve)
            .catch(reject);
        });
      });

      const result = await criarReserva(brand, {
        ...formData,
        quantidade_pessoas: parseInt(formData.quantidade_pessoas),
        captchaToken
      });

      if (result && result.success) {
        setSucesso(true);
      } else {
        setErro(result?.error || result?.message || 'Erro ao enviar reserva. Tente novamente.');
      }
    } catch (error) {
      setErro('Erro ao enviar reserva. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (sucesso) {
    return (
      <div className="bg-light" style={{ backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
        <div className="container py-3">
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="p-4 text-center" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <Lottie animationData={fazerReservaAnimation} loop style={{ width: 200, height: 200, margin: '0 auto' }} />
                <h4 style={{ fontWeight: 700, color: '#1a1a1a' }}>Reserva em Análise</h4>
                <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                  Seu pedido de reserva foi enviado. Em breve, alguém do nosso time de <br></br><strong>{nomeLoja || 'nosso restaurante'}</strong> entrará em contato para confirmar detalhes.
                </p>
                <div className="mt-3 p-3" style={{ background: '#f9fafb', borderRadius: '8px', textAlign: 'left' }}>
                  <div className="mb-2"><strong>Nome:</strong> {formData.nome_cliente}</div>
                  <div className="mb-2"><strong>Data:</strong> {formData.data_reserva.split('-').reverse().join('/')}</div>
                  <div className="mb-2"><strong>Horário:</strong> {formData.horario}</div>
                  <div><strong>Pessoas:</strong> {formData.quantidade_pessoas}</div>
                </div>
                <div className="mt-4 d-grid gap-2">
                  <button className="btn w-100" onClick={() => { setSucesso(false); setFormData({ nome_cliente: '', celular: '', data_reserva: '', horario: '', quantidade_pessoas: '', observacoes: '' }); }} style={{ borderRadius: '8px', padding: '12px', fontWeight: 600, backgroundColor: '#0039ff', color: '#fff', border: 'none' }}>
                    Fazer Nova Reserva
                  </button>
                  <Link to="/" className="btn w-100" style={{ borderRadius: '8px', padding: '12px', fontWeight: 500, backgroundColor: '#fff', color: '#374151', border: '1px solid #e5e7eb' }}>
                    Voltar para a Loja
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light" style={{ backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
      <div className="container py-3">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            {/* Header */}
            <div className="mb-3 d-flex align-items-center" style={{ gap: '12px' }}>
              <button className="btn btn-link p-0 text-dark" onClick={() => navigate(-1)}>
                <ArrowLeft width={24} height={24} />
              </button>
              <h5 className="mb-0" style={{ fontWeight: 700 }}></h5>
            </div>

            {/* Header logos */}
            <div className="text-center mb-3">
              <div className="d-flex justify-content-center align-items-center mb-3" style={{ position: 'relative', width: '106px', height: '60px', margin: '0 auto 12px' }}>
                <img
                  src="https://grubicons.s3.us-east-1.amazonaws.com/logo-grub.png"
                  alt="Logo GRUB"
                  style={{ position: 'absolute', right: 0, width: '60px', height: '60px', borderRadius: '50%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 1 }}
                />
                {brandLogoUrl && (
                  <img
                    src={`https://grubupload.s3.amazonaws.com/${brandLogoUrl}`}
                    alt="Logo da marca"
                    style={{ position: 'absolute', left: 0, width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
              <h1 style={{ fontWeight: 800, fontSize: '1.5rem', color: '#1a1a1a' }}>Reservar uma mesa</h1>
              <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>Preencha os dados abaixo e garanta seu lugar.</p>
            </div>

            {/* Form */}
            <div className="p-3" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
              {erro && (
                <div className="alert alert-danger py-2 px-3" style={{ fontSize: '0.9rem', borderRadius: '8px' }}>
                  {erro}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted d-block text-start">
                    Seu Nome *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="nome_cliente"
                    value={formData.nome_cliente}
                    onChange={handleChange}
                    placeholder="Nome completo"
                    required
                    style={{ borderRadius: '8px' }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted d-block text-start">
                    Celular *
                  </label>
                  <PhoneInput
                    country="br"
                    value={formData.celular}
                    onChange={(value) => setFormData(prev => ({ ...prev, celular: value }))}
                    inputClass="phone-input-field"
                    buttonClass="phone-input-btn"
                    preferredCountries={['br', 'us', 'ar', 'pt']}
                    enableSearch
                    searchPlaceholder="Buscar país..."
                    searchNotFound="País não encontrado"
                  />
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-bold text-muted d-block text-start">
                      Data *
                    </label>
                    <input
                      type="date"
                      className={`form-control ${formData.data_reserva && !isDiaPermitido(formData.data_reserva, diasPermitidosReserva) ? 'is-invalid' : ''}`}
                      name="data_reserva"
                      value={formData.data_reserva}
                      onChange={handleChange}
                      min={getTodayString()}
                      required
                      style={{ borderRadius: '8px' }}
                    />
                    {formData.data_reserva && !isDiaPermitido(formData.data_reserva, diasPermitidosReserva) && (
                      <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                        Reservas não disponíveis para {NOMES_DIAS[new Date(formData.data_reserva + 'T12:00:00').getDay()]}
                      </div>
                    )}
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-bold text-muted d-block text-start">
                      Horário *
                    </label>
                    <select
                      className="form-select"
                      name="horario"
                      value={formData.horario}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: '8px' }}
                    >
                      <option value="">Selecione</option>
                      {(() => {
                        // Verificar se o dia é permitido para reservas
                        if (formData.data_reserva && !isDiaPermitido(formData.data_reserva, diasPermitidosReserva)) {
                          return <option value="" disabled>Reservas não disponíveis neste dia</option>;
                        }
                        if (!formData.data_reserva || horariosLoja.length === 0) {
                          // Fallback: slots com intervalo configurado das 08:00 às 23:00
                          return gerarSlots('08:00', '23:00', intervaloMinutos).map(h => (
                            <option key={h} value={h}>{h}</option>
                          ));
                        }
                        const date = new Date(formData.data_reserva + 'T12:00:00');
                        const bitmask = DIAS_BITMASK[date.getDay()];
                        
                        // Buscar TODOS os horários do dia (múltiplos turnos)
                        // Nota: ativo pode vir como número (0/1) ou booleano
                        const horariosDoDia = horariosLoja.filter(h => h.dia === bitmask && (h.ativo === true || h.ativo === 1));
                        
                        if (horariosDoDia.length === 0) {
                          return <option value="" disabled>Loja fechada neste dia</option>;
                        }
                        
                        // Gerar slots para cada turno e combinar
                        const todosSlots = [];
                        horariosDoDia.forEach(horario => {
                          const hi = horario.horario_inicial ? horario.horario_inicial.substring(0, 5) : '08:00';
                          const hf = horario.horario_final ? horario.horario_final.substring(0, 5) : '23:00';
                          const slots = gerarSlots(hi, hf, intervaloMinutos);
                          slots.forEach(slot => {
                            if (!todosSlots.includes(slot)) {
                              todosSlots.push(slot);
                            }
                          });
                        });
                        
                        // Ordenar slots
                        todosSlots.sort();
                        
                        return todosSlots.map(h => (
                          <option key={h} value={h}>{h}</option>
                        ));
                      })()}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted d-block text-start">
                    Quantidade de Pessoas *
                  </label>
                  <select
                    className="form-select"
                    name="quantidade_pessoas"
                    value={formData.quantidade_pessoas}
                    onChange={handleChange}
                    required
                    style={{ borderRadius: '8px' }}
                  >
                    <option value="">Selecione</option>
                    {Array.from({ length: 50 }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'pessoa' : 'pessoas'}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted d-block text-start">
                    Observações
                  </label>
                  <textarea
                    className="form-control"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows="3"
                    maxLength={500}
                    placeholder="Alguma preferência ou informação adicional?"
                    style={{ borderRadius: '8px' }}
                  />
                  <small className="text-muted">{formData.observacoes.length}/500</small>
                </div>

                <button
                  type="submit"
                  className="btn w-100"
                  disabled={enviando}
                  style={{ borderRadius: '8px', padding: '12px', fontWeight: 600, backgroundColor: '#0039ff', color: '#fff', border: 'none' }}
                >
                  {enviando ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                  ) : (
                    <><SendDiagonal width={16} height={16} className="me-2" />Enviar Reserva</>
                  )}
                </button>
                <div className="d-flex justify-content-center mt-3">
                  <img src={require('../assets/powered-by-grub_2.png')} alt="Powered by Grub" style={{ height: '34px' }} />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reserva;
